import { Dispatch } from 'react';
import { api } from '../root';
import { addNotification } from '../../store/notifications/slice';
import { handleRequestErrors } from 'helpers/functions/commons';
import { 
    Parameter, 
    CreateParameterRequest, 
    UpdateParameterRequest, 
    GetParametersRequest,
    GetLibraryParametersRequest,
    ParameterResponse,
    GetTagAttributesForCalculatorRequest,
    TagAttributesForCalculatorResponse
} from './types';

/**
 * Fetch user's own parameters (not library parameters)
 */
export const R_getParameters = async (request: GetParametersRequest, thunkAPI: unknown) => {
    try {
        const params = new URLSearchParams();
        if (request.assetType) params.append('assetType', request.assetType);
        if (request.userId) params.append('userId', request.userId);
        
        const response = await api.get<ParameterResponse | Parameter[]>(`/PropertyCalculator/parameters?${params.toString()}`);
        
        // Handle both response formats: object with myParameters/libraryParameters OR flat array
        let result: ParameterResponse;
        
        if (Array.isArray(response.data)) {
            // Backend returned a flat array - these are user's parameters only
            const myParams = response.data as Parameter[];
            result = {
                myParameters: myParams,
                libraryParameters: [] // Empty since we fetch library parameters separately
            };
        } else {
            // Backend returned the expected object structure
            result = response.data as ParameterResponse;
        }
        
        return result;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e);
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> };
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
};

/**
 * Fetch library parameters (parameters created by all users)
 */
export const R_getLibraryParameters = async (request: GetLibraryParametersRequest, thunkAPI: unknown) => {
    try {
        const params = new URLSearchParams();
        if (request.assetType) params.append('assetType', request.assetType);
        
        const response = await api.get<Parameter[]>(`/PropertyCalculator/library-parameters?${params.toString()}`);
        return response.data;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e);
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> };
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
};

/**
 * Get a single parameter by ID
 */
export const R_getParameterById = async (id: string, thunkAPI: unknown) => {
    try {
        const response = await api.get<Parameter>(`/PropertyCalculator/parameters/${id}`);
        return response.data;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e);
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> };
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
};

/**
 * Create a new parameter
 */
export const R_createParameter = async (data: CreateParameterRequest, thunkAPI: unknown) => {
    try {
        const response = await api.post<Parameter>('/PropertyCalculator/parameters', data);
        const thunkSuccess = thunkAPI as { dispatch: Dispatch<unknown> };
        thunkSuccess.dispatch(addNotification({ 
            type: 'success', 
            message: 'Parameter created successfully' 
        }));
        return response.data;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e);
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> };
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
};

/**
 * Update an existing parameter
 */
export const R_updateParameter = async (data: UpdateParameterRequest, thunkAPI: unknown) => {
    try {
        const response = await api.put<Parameter>(`/PropertyCalculator/parameters/${data.id}`, data);
        const thunkSuccess = thunkAPI as { dispatch: Dispatch<unknown> };
        thunkSuccess.dispatch(addNotification({ 
            type: 'success', 
            message: 'Parameter updated successfully' 
        }));
        return response.data;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e);
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> };
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
};

/**
 * Delete a parameter
 */
export const R_deleteParameter = async (id: string, thunkAPI: unknown) => {
    try {
        await api.delete(`/PropertyCalculator/parameters/${id}`);
        const thunkSuccess = thunkAPI as { dispatch: Dispatch<unknown> };
        thunkSuccess.dispatch(addNotification({ 
            type: 'success', 
            message: 'Parameter deleted successfully' 
        }));
        return id;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e);
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> };
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
};

/**
 * Copy a parameter (create a new one based on existing)
 */
export const R_copyParameter = async (data: { id: string; userId: string }, thunkAPI: unknown) => {
    try {
        const response = await api.post<Parameter>(`/PropertyCalculator/parameters/${data.id}/copy`, {
            userId: data.userId
        });
        const thunkSuccess = thunkAPI as { dispatch: Dispatch<unknown> };
        thunkSuccess.dispatch(addNotification({ 
            type: 'success', 
            message: 'Parameter copied successfully' 
        }));
        return response.data;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e);
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> };
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
};

/**
 * Get asset types available for the user's license
 */
export const R_getAvailableAssetTypes = async (thunkAPI: unknown) => {
    try {
        const response = await api.get<string[]>('/PropertyCalculator/asset-types');
        return response.data;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e);
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> };
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
};

export const R_getTagAttributesForCalculator = async (request: GetTagAttributesForCalculatorRequest, thunkAPI: unknown) => {
    try {
        const params = new URLSearchParams();
        if (request.category !== undefined) params.append('category', request.category.toString());
        if (request.type !== undefined) params.append('type', request.type.toString());
        
        const queryString = params.toString();
        const url = queryString 
            ? `/PropertyCalculator/getFullTagAttributeParameterlist?${queryString}`
            : `/PropertyCalculator/getFullTagAttributeParameterlist`;
        
        const response = await api.get<TagAttributesForCalculatorResponse>(url);
        return response.data.tagAttributes;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e);
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> };
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
};

